import { useEffect, useRef, useState } from "react";
import {
  ExternalLink,
  Play,
  Pause,
  Loader2,
  Volume2,
  VolumeX,
  MessageCircle,
  Info,
  Heart,
} from "lucide-react";
import { format } from "date-fns";
import type { SocialAccount, SocialPost } from "@/services/socialFeedService";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SocialPostComments } from "./SocialPostComments";
import { useSound } from "./soundUnlock";
import { cdnImage, cdnWidthSet } from "@/lib/images";
import { resolved, failedOnce, primePlayable, warmBytes } from "./mediaCache";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

interface Props {
  post: SocialPost;
  account?: SocialAccount | undefined;
  active: boolean;
  /** Warm upcoming videos without slowing the playing one down. */
  warm: boolean;
  autoScroll: boolean;
  liked: boolean;
  likeCount: number;
  onToggleLike: () => void;
  onEnded: () => void;
}

function clock(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function SocialFeedItem({
  post,
  account,
  active,
  warm,
  autoScroll,
  liked,
  likeCount,
  onToggleLike,
  onEnded,
}: Props) {
  const t = useT();
  const [time, setTime] = useState({ current: 0, duration: 0 });
  const [sheet, setSheet] = useState<"comments" | "about" | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [src, setSrc] = useState<string | null>(resolved.get(post.id) ?? null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(failedOnce.has(post.id));
  const [paused, setPaused] = useState(false);
  // False until the video has enough data to show a frame: keeps a skeleton on screen.
  const [ready, setReady] = useState(false);
  const [burst, setBurst] = useState<{ x: number; y: number; key: number } | null>(null);
  // 0 = our stored copy, 1 = the creator's original picture, 2 = no cover.
  const [coverStage, setCoverStage] = useState<0 | 1 | 2>(0);
  const lastTap = useRef(0);
  const { soundOn, setSoundOn, markSoundLocked } = useSound();

  const platform = account?.platform ?? "social";
  const creator = account?.display_name || (account ? `@${account.handle}` : "Creator");
  const isVideo = post.media_type === "video";

  const load = async () => {
    if (resolved.has(post.id)) {
      setSrc(resolved.get(post.id) ?? null);
      return;
    }
    setLoading(true);
    setFailed(false);
    await primePlayable([post.id]);
    const url = resolved.get(post.id);
    if (url) setSrc(url);
    else setFailed(true);
    setLoading(false);
  };

  // A new source means a new load: show the skeleton again.
  useEffect(() => {
    setReady(false);
  }, [src]);

  // Prepare this video as soon as it is close to the viewport.
  useEffect(() => {
    if (!isVideo) return;
    if (resolved.has(post.id)) {
      setSrc(resolved.get(post.id) ?? null);
      if (warm && !active) warmBytes(post.id);
      return;
    }
    if ((active || warm) && !failedOnce.has(post.id)) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVideo, active, warm]);

  // Play the active one, pause the rest. Respects a manual pause.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active && !paused) {
      video.play().catch(() => {
        // Browser blocked sound: mute, keep playing, unlock on first gesture.
        if (!video.muted) {
          markSoundLocked();
          video.muted = true;
          void video.play().catch(() => undefined);
        }
      });
    } else {
      video.pause();
      if (!active) {
        video.currentTime = 0;
        setPaused(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, paused, src]);

  const togglePaused = () => setPaused((p) => !p);

  const likeWithBurst = (x: number, y: number) => {
    setBurst({ x, y, key: Date.now() });
    window.setTimeout(() => setBurst(null), 800);
    if (!liked) onToggleLike();
  };

  const handleSurfaceTap = (e: React.MouseEvent<HTMLDivElement>) => {
    const now = Date.now();
    const rect = e.currentTarget.getBoundingClientRect();
    if (now - lastTap.current < 320) {
      lastTap.current = 0;
      likeWithBurst(e.clientX - rect.left, e.clientY - rect.top);
      return;
    }
    lastTap.current = now;
  };

  const postedAt = post.posted_at ? format(new Date(post.posted_at), "d MMM yyyy") : null;

  // Covers come from TikTok/Instagram at full size; resize them to the phone box.
  // Stage 0: our own stored copy. Stage 1: the creator's original picture.
  // Stage 2: no cover at all, so the dark skeleton shows (never the grey
  // generic placeholder, which looks broken on a full-screen player).
  const noFallback = (url: string) => `${url}${url.includes("?") ? "&" : "?"}fb=none`;
  const coverSrc =
    post.thumbnail_url && coverStage < 2
      ? coverStage === 0
        ? noFallback(cdnImage(post.thumbnail_url, { width: 480, quality: 60 }))
        : post.thumbnail_url
      : undefined;
  const coverSet =
    post.thumbnail_url && coverStage === 0
      ? (cdnWidthSet(post.thumbnail_url, [320, 480, 720], { quality: 60 }) ?? "")
          .split(", ")
          .map((part) => {
            const [u, w] = part.split(" ");
            return u ? `${noFallback(u)} ${w ?? ""}`.trim() : part;
          })
          .join(", ")
      : undefined;
  // Tiny blurred stand-in so the frame is never an empty black box.
  const coverBlur =
    post.thumbnail_url && coverStage === 0
      ? noFallback(cdnImage(post.thumbnail_url, { width: 24, quality: 30 }))
      : undefined;

  const aboutBlock = (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={account?.avatar_url ?? undefined} alt={creator} />
          <AvatarFallback>{creator.replace("@", "").slice(0, 1).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{creator}</p>
          <p className="text-xs capitalize text-muted-foreground">
            {platform}
            {postedAt ? ` · ${postedAt}` : ""}
          </p>
        </div>
      </div>

      {post.caption ? (
        <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
          {post.caption}
        </p>
      ) : null}

      {failed ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground"><T k="auto.this-one-cannot-be-played-here-c71c" /></p>
          <Button asChild size="sm" variant="secondary">
            <a href={post.post_url} target="_blank" rel="noopener noreferrer">
              Watch on {platform === "tiktok" ? "TikTok" : "Instagram"}
            </a>
          </Button>
        </div>
      ) : (
        <a
          href={post.post_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
        >
          View original <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );

  return (
    <section className="relative flex h-full w-full snap-start snap-always flex-col overflow-hidden bg-black lg:flex-row">
      {/* Player: full bleed on phones, framed beside the panel on desktop */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center bg-black lg:p-4">
        <div className="relative flex h-full w-full max-h-full items-center justify-center lg:w-auto">
          <div
            onClick={handleSurfaceTap}
            className="relative h-full w-full overflow-hidden bg-black lg:w-auto lg:rounded-xl lg:aspect-[9/16]"
          >
            {/* Blurred stand-in: shows instantly while the real cover loads */}
            {coverBlur ? (
              <div
                aria-hidden
                style={{ backgroundImage: `url(${coverBlur})` }}
                className="absolute inset-0 scale-110 bg-cover bg-center blur-xl"
              />
            ) : null}

            {/* Cover layer under the video: falls back on its own, so a
                missing stored copy never paints the grey placeholder. */}
            {isVideo && coverSrc && !ready ? (
              <img
                aria-hidden
                src={coverSrc}
                srcSet={coverSet}
                sizes="(max-width: 1024px) 100vw, 420px"
                alt=""
                decoding="async"
                onError={() => setCoverStage((s) => (s < 2 ? ((s + 1) as 1 | 2) : s))}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : null}

            {isVideo && src ? (
              <video
                ref={videoRef}
                src={src}
                autoPlay={active}
                muted={!soundOn}
                playsInline
                loop={!autoScroll}
                preload={active || warm ? "auto" : "metadata"}
                onTimeUpdate={(e) =>
                  setTime({
                    current: e.currentTarget.currentTime,
                    duration: e.currentTarget.duration,
                  })
                }
                onLoadedMetadata={(e) =>
                  setTime({ current: 0, duration: e.currentTarget.duration })
                }
                onLoadedData={() => setReady(true)}
                onCanPlay={() => setReady(true)}
                onPlaying={() => setReady(true)}
                onEnded={() => {
                  if (autoScroll && active) onEnded();
                }}
                onError={() => {
                  resolved.delete(post.id);
                  failedOnce.add(post.id);
                  setSrc(null);
                  setReady(false);
                  setFailed(true);
                }}
                className="relative h-full w-full object-cover"
              />
            ) : coverSrc ? (
              <img
                src={coverSrc}
                srcSet={coverSet}
                sizes="(max-width: 1024px) 100vw, 420px"
                alt={post.caption?.slice(0, 90) || `${platform} post by ${creator}`}
                loading={active ? "eager" : "lazy"}
                decoding="async"
                onError={() => setCoverStage((s) => (s < 2 ? ((s + 1) as 1 | 2) : s))}
                {...(active ? { fetchPriority: "high" as const } : {})}
                className="relative h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/60">
                <Play className="h-10 w-10" />
              </div>
            )}

            {/* Skeleton: stays until the video can actually show a frame. */}
            {isVideo && !ready && !failed ? (
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-0 animate-pulse bg-gradient-to-b from-white/10 via-white/5 to-white/10 ${
                  coverSrc ? "opacity-60" : ""
                }`}
              >
                <div className="absolute bottom-6 left-4 right-20 space-y-3">
                  <div className="h-4 w-1/3 rounded bg-white/15" />
                  <div className="h-3 w-3/4 rounded bg-white/10" />
                  <div className="h-3 w-1/2 rounded bg-white/10" />
                </div>
                <div className="absolute bottom-6 right-4 space-y-4">
                  <div className="h-11 w-11 rounded-full bg-white/15" />
                  <div className="h-11 w-11 rounded-full bg-white/10" />
                  <div className="h-11 w-11 rounded-full bg-white/10" />
                </div>
              </div>
            ) : null}

            {isVideo && !src ? (
              <button
                type="button"
                onClick={() => void load()}
                aria-label={t("auto.play-this-video-a220")}
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition hover:bg-black/40"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 shadow-lg">
                  {loading ? (
                    <Loader2 className="h-8 w-8 animate-spin text-black" />
                  ) : (
                    <Play className="ml-1 h-8 w-8 text-black" fill="currentColor" />
                  )}
                </span>
              </button>
            ) : null}

            <Badge className="absolute left-3 top-3 capitalize">{platform}</Badge>

            {burst ? (
              <span
                key={burst.key}
                style={{ left: burst.x, top: burst.y }}
                className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 animate-ping text-white drop-shadow-lg"
              >
                <Heart className="h-20 w-20" fill="currentColor" />
              </span>
            ) : null}

            {isVideo && src && paused ? (
              <button
                type="button"
                onClick={togglePaused}
                aria-label={t("auto.resume-video-3494")}
                className="absolute inset-0 z-10 flex items-center justify-center"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur">
                  <Play className="ml-1 h-7 w-7" fill="currentColor" />
                </span>
              </button>
            ) : null}

            {isVideo && src ? (
              <button
                type="button"
                onClick={togglePaused}
                aria-label={paused ? "Play video" : "Pause video"}
                className="absolute bottom-14 left-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur"
              >
                {paused ? (
                  <Play className="ml-0.5 h-5 w-5" fill="currentColor" />
                ) : (
                  <Pause className="h-5 w-5" fill="currentColor" />
                )}
              </button>
            ) : null}

            {isVideo && src ? (
              <button
                type="button"
                onClick={() => setSoundOn(!soundOn)}
                aria-label={soundOn ? "Turn sound off" : "Turn sound on"}
                className="absolute right-3 top-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-2 text-xs font-medium text-white backdrop-blur"
              >
                {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                {soundOn ? null : <span><T k="auto.tap-for-sound-a80b" /></span>}
              </button>
            ) : null}

            {/* Actions over the video: like everywhere, comments and about on phones */}
            <div className="absolute bottom-24 right-3 z-10 flex flex-col items-center gap-4">
              <button
                type="button"
                onClick={onToggleLike}
                aria-pressed={liked}
                aria-label={liked ? "Remove like" : "Like this video"}
                className="flex flex-col items-center gap-1 text-white"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55 backdrop-blur">
                  <Heart
                    className={`h-6 w-6 ${liked ? "text-red-500" : ""}`}
                    fill={liked ? "currentColor" : "none"}
                  />
                </span>
                <span className="text-xs font-semibold">{likeCount}</span>
              </button>
              <button
                type="button"
                onClick={() => setSheet("comments")}
                aria-label={t("auto.open-comments-b1b9")}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur lg:hidden"
              >
                <MessageCircle className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={() => setSheet("about")}
                aria-label={t("auto.about-this-post-3fd2")}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur lg:hidden"
              >
                <Info className="h-6 w-6" />
              </button>
            </div>

            {isVideo && src ? (
              <div className="absolute inset-x-0 bottom-0">
                <div className="h-1 w-full bg-white/20">
                  <div
                    className="h-full bg-white"
                    style={{
                      width: time.duration
                        ? `${Math.min(100, (time.current / time.duration) * 100)}%`
                        : "0%",
                    }}
                  />
                </div>
                <div className="px-3 py-2 text-xs font-medium text-white/90">
                  {clock(time.current)} / {clock(time.duration)}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Phone sheets */}
      <Sheet open={sheet !== null} onOpenChange={(open) => setSheet(open ? sheet : null)}>
        <SheetContent
          side="bottom"
          className="h-[85dvh] rounded-t-2xl p-0 lg:hidden"
        >
          <SheetHeader className="px-5 pt-5">
            <SheetTitle>{sheet === "about" ? "About" : "Comments"}</SheetTitle>
          </SheetHeader>
          <div className="h-[calc(85dvh-5rem)] overflow-y-auto px-5 pb-8 pt-4">
            {sheet === "about" ? aboutBlock : <SocialPostComments postId={post.id} />}
          </div>
        </SheetContent>
      </Sheet>

      {/* Info and comments (desktop) */}
      <aside className="hidden w-full shrink-0 flex-col overflow-hidden border-t bg-background lg:flex lg:h-full lg:w-[380px] lg:border-l lg:border-t-0">
        <Tabs defaultValue="comments" className="flex h-full min-h-0 flex-col">
          <TabsList className="m-3 grid w-auto grid-cols-2">
            <TabsTrigger value="comments"><T k="auto.comments-fce0" /></TabsTrigger>
            <TabsTrigger value="about"><T k="auto.about-6b21" /></TabsTrigger>
          </TabsList>

          <TabsContent value="comments" className="mt-0 min-h-0 flex-1 overflow-y-auto px-5 pb-5">
            <SocialPostComments postId={post.id} />
          </TabsContent>

          <TabsContent value="about" className="mt-0 min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-5">
            {aboutBlock}
          </TabsContent>
        </Tabs>
      </aside>
    </section>
  );
}
